import { useQuery } from '@tanstack/react-query';
import {
  DIAM_API_PATH,
  DIAM_PAGE_LIMIT,
  DIAM_TEAM_ID,
  getTeamById,
  getTeamMembership,
  getTeamRolesPage,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import { mapAssignRoleTree, mapAssignTeamTree } from './mapAssignRoleTree';

const TEAM_ROLES_FILTER = { type: 'TEAM' as const };

export function useAssignIdentityDrawerOptions(
  identityId: string | undefined,
  isOpen: boolean,
) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);

  return useQuery({
    queryKey: [
      'diam',
      'assign-identity-drawer',
      DIAM_TEAM_ID,
      identityId ?? '',
      TEAM_ROLES_FILTER,
    ] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      const [team, rolesPage, membership] = await Promise.all([
        getTeamById(apiUrl, idToken, DIAM_TEAM_ID),
        getTeamRolesPage(apiUrl, idToken, DIAM_TEAM_ID, {
          page: 0,
          limit: DIAM_PAGE_LIMIT,
          filter: TEAM_ROLES_FILTER,
        }),
        identityId
          ? getTeamMembership(apiUrl, idToken, identityId).catch(
              () => undefined,
            )
          : Promise.resolve(undefined),
      ]);

      const assignedRoleIds = new Set(
        (membership?.roles ?? []).map(role => role.roleId),
      );
      const roles = rolesPage.elements ?? [];

      return {
        teamId: DIAM_TEAM_ID,
        teamName: team.name,
        teamTree: mapAssignTeamTree(DIAM_TEAM_ID, team.name),
        rolesTree: mapAssignRoleTree(roles, assignedRoleIds),
        assignableRoleCount: roles.filter(
          role => !assignedRoleIds.has(role.roleId),
        ).length,
      };
    },
    enabled: isSignedIn && isOpen && Boolean(identityId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}
